import { IF, IS, nil, NO, UIButton, UIColor, UIImageView, UITextView, UIView, YES } from "uicore-ts"
import { LooseObject } from "uicore-ts/scripts/UIView"
import { CBColor } from "./CB/CBColor"


export type CellViewVariant = "label" | "button" | "custom"


export class CellView<DataType extends object = object, CustomViewType extends UIView = UIView> extends UIView {
    
    
    _variant: CellViewVariant = "label"
    
    _titleLabel?: UITextView
    _buttonView?: UIButton
    _customView?: CustomViewType
    // The data item a selection cell was last updated with, so its toggle handler can read it.
    _data?: object
    
    _rightImageView?: UIImageView

    readonly sizeOfRightImage = 16

    get gapBetweenTitleAndRightImage(): number {
        return (this.core.paddingLength * 0.25).integerValue
    }

    // When YES, layoutSubviews insets BOTH horizontal sides by the sort-caret allowance
    // so a centre-aligned title stays centred over the full cell. Set by CBTableHeaderView
    // from the descriptor's titleLabelAlignment (a UIButton centres its title via layout,
    // not the label's text-align, so this can't be reliably read back at layout time).
    centersTitleWithinRightImageInset = NO

    minHeight = 50
    
    _pendingText?: string
    _pendingTextAlignment?: keyof typeof UITextView.textAlignment
    
    
    constructor(elementID: string = ("UIView" + UIView.nextIndex),
                viewHTMLElement: HTMLElement & LooseObject | null = null,
                elementType: string | null = null,
                preInitConfiguratorObject?: ConstructorParameters<typeof UIView>[3]) {
        
        super(elementID, viewHTMLElement, elementType, preInitConfiguratorObject)
        
        this.contentInsets = { left: 5, right: 5, bottom: 5, top: 5 }
        
        this._setupCurrentVariant()
        
    }
    
    
    get variant(): CellViewVariant {
        return this._variant
    }
    
    set variant(variant: CellViewVariant) {
        
        if (this._variant == variant) {
            return
        }
        
        this._tearDownCurrentVariant()
        this._variant = variant
        this._setupCurrentVariant()
        this.setNeedsLayout()
        
    }
    
    
    get isAButton(): boolean {
        return this._variant == "button"
    }
    
    set isAButton(isAButton: boolean) {
        
        if (isAButton) {
            this.variant = "button"
        }
        else {
            this.variant = "label"
        }
        
    }
    
    
    get titleLabel(): UITextView | undefined {
        
        if (this._variant == "button") {
            return this._buttonView?.titleLabel
        }
        
        return this._titleLabel
        
    }
    
    get buttonView(): UIButton | undefined {
        return this._buttonView
    }
    
    get customView(): CustomViewType | undefined {
        return this._customView
    }
    
    set customView(customView: CustomViewType) {
        this._tearDownCurrentVariant()
        this._variant = "custom"
        this._customView = customView
        customView.addedAsSubviewToView(this)
        this.setNeedsLayout()
    }
    
    
    initializeLabelVariant(titleLabelType: string = UITextView.type.span) {
        this._tearDownCurrentVariant()
        this._variant = "label"
        this._titleLabel = new UITextView(this.elementID + "TitleLabel", titleLabelType)
        this._titleLabel.configureWithObject({
            userInteractionEnabled: YES,
            nativeSelectionEnabled: YES,
            textAlignment: this._pendingTextAlignment ?? UITextView.textAlignment.left,
            text: this._pendingText ?? ""
        })
        this._pendingText = undefined
        this._pendingTextAlignment = undefined
        this._titleLabel.addedAsSubviewToView(this)
        this.setNeedsLayout()
    }
    
    
    private _setupCurrentVariant() {
        
        if (this._variant == "label") {
            
            this._titleLabel = new UITextView(this.elementID + "TitleLabel", UITextView.type.span)
            this._titleLabel.configureWithObject({
                userInteractionEnabled: YES,
                nativeSelectionEnabled: YES,
                textAlignment: this._pendingTextAlignment ?? UITextView.textAlignment.left,
                text: this._pendingText ?? ""
            })
            this._titleLabel.addedAsSubviewToView(this)
            
        }
        else if (this._variant == "button") {
            
            this._buttonView = new UIButton(this.elementID + "ButtonView")
            this._buttonView.configureWithObject({
                nativeSelectionEnabled: NO,
                colors: {
                    titleLabel: {
                        normal: CBColor.primaryTintColor,
                        highlighted: CBColor.primaryTintColor,
                        selected: CBColor.primaryTintColor
                    },
                    background: {
                        normal: CBColor.transparentColor,
                        hovered: new UIColor("#f8f8f8"),
                        highlighted: new UIColor("#ebebeb"),
                        selected: new UIColor("#ebebeb")
                    }
                },
                style: { cursor: "pointer", outline: "" }
            })
            this._buttonView.titleLabel.configureWithObject({
                userInteractionEnabled: NO,
                nativeSelectionEnabled: NO,
                textAlignment: this._pendingTextAlignment ?? UITextView.textAlignment.center,
                text: this._pendingText ?? ""
            })
            this._buttonView.addedAsSubviewToView(this)
            
        }
        
        this._pendingText = undefined
        this._pendingTextAlignment = undefined
        
    }
    
    
    private _tearDownCurrentVariant() {
        
        this._pendingText = this.titleLabel?.text
        this._pendingTextAlignment = this.titleLabel?.textAlignment
        
        this._titleLabel?.removeFromSuperview()
        this._titleLabel = undefined
        
        this._buttonView?.removeFromSuperview()
        this._buttonView = undefined
        
        this._customView?.removeFromSuperview()
        this._customView = undefined
        
    }
    
    
    get text(): string {
        return this.titleLabel?.text ?? ""
    }
    
    set text(text: string) {
        
        if (this._variant == "label") {
            
            if (!this._titleLabel) {
                this.initializeLabelVariant()
            }
            
            this._titleLabel!.text = text
            
        }
        else if (this._variant == "button" && this._buttonView) {
            this._buttonView.titleLabel.text = text
        }
        
    }
    
    
    updateCellContent(dataItem: DataType) {
        // Override hook for CBTableRowView subclasses
    }
    
    
    get rightImageSource(): string {
        return this._rightImageView?.imageSource ?? nil
    }
    
    set rightImageSource(imageSource: string) {
        
        if (IS(imageSource)) {
            
            if (!IS(this._rightImageView)) {
                this._rightImageView = new UIImageView(this.elementID + "RightImageView").configuredWithObject({
                    userInteractionEnabled: NO
                })
            }
            
            this._rightImageView!.imageSource = imageSource
            this.addSubview(this._rightImageView!)
            
        }
        else {
            
            this._rightImageView?.removeFromSuperview()
            
        }
        
    }
    
    
    /**
     * Wraps and clamps this cell's title label to `numberOfLines` lines (default 2),
     * so a long value (e.g. a procurement title) shows across a couple of lines and
     * is then ellipsized, keeping the row height tidy instead of letting one long
     * value blow up the row. The full value stays available through the row's hover
     * tooltip (see RHTableRowView._updateCellTooltips).
     */
    configureAsClampedMultilineText(numberOfLines: number = 2) {

        const label = this.titleLabel
        if (!label) {
            return
        }

        label.configureWithObject({
            isSingleLine: NO,
            style: { whiteSpace: "normal" }
        })

        label.textElementView.configureWithObject({
            style: {
                whiteSpace: "normal",
                display: "-webkit-box",
                webkitLineClamp: "" + numberOfLines,
                webkitBoxOrient: "vertical",
                overflow: "hidden"
            }
        })

    }


    configureAsTextLinkWithNormalTextColor(normalTextColor: UIColor): void {
        const button = this.buttonView
        if (!button) {
            return
        }
        button.configureWithObject({
            colors: {
                titleLabel: {
                    normal: normalTextColor,
                    hovered: button.colors.titleLabel.hovered ?? button.colors.titleLabel.normal
                }
            }
        })
        button.viewHTMLElement.setAttribute("role", "link")
        button.updateContentForCurrentState()
    }


    /**
     * Like {@link configureAsClampedMultilineText} but with NO line cap: the value
     * wraps to as many lines as it needs and the row grows to fit it, so nothing is
     * ever ellipsized. Use when truncation is unacceptable and the content is bounded
     * (e.g. a short role list), where a tooltip-only fallback would not be acceptable.
     */
    configureAsWrappingMultilineText() {

        const label = this.titleLabel
        if (!label) {
            return
        }

        label.configureWithObject({
            isSingleLine: NO,
            style: { whiteSpace: "normal" }
        })

        label.textElementView.configureWithObject({
            style: { whiteSpace: "normal" }
        })

    }


    configureAsMultilineTextPreservingLineBreaks() {

        const label = this.titleLabel
        if (!label) {
            return
        }

        label.configureWithObject({
            isSingleLine: NO,
            style: {
                overflowWrap: "anywhere",
                whiteSpace: "pre-line"
            }
        })

        label.textElementView.configureWithObject({
            style: {
                overflowWrap: "anywhere",
                whiteSpace: "pre-line"
            }
        })

    }


    override layoutSubviews() {

        super.layoutSubviews()

        const bounds = this.contentBounds
        
        const activeView: UIView | undefined = IF(this._variant == "label")(() =>
            this._titleLabel as UIView | undefined
        ).ELSE_IF(this._variant == "button")(() =>
            this._buttonView as UIView | undefined
        ).ELSE(() =>
            this._customView as UIView | undefined
        )
        
        if (activeView) {
            activeView.frame = bounds
        }
        
        if (this._rightImageView && this._rightImageView.superview == this) {

            // The sort caret always sits AFTER the title. A centred title reserves the caret's slot
            // on both sides so it stays centred without overlapping the icon. A right-aligned title
            // reserves the slot on the right; a left-aligned title keeps the full-width cell and gets
            // the caret overlaid just past its trailing edge.
            const caretSize = this.sizeOfRightImage
            const caretGap = this.gapBetweenTitleAndRightImage
            const label = this.titleLabel
            const buttonContentInset = this._buttonView?.contentPadding ?? 0
            const titleIsRightAligned = label?.textAlignment === UITextView.textAlignment.right
            const titleWidth = Math.min(
                label?.intrinsicContentWidth(bounds.height) ?? 0,
                Math.max(0, bounds.width - caretSize - caretGap)
            )

            if (activeView) {
                activeView.frame = IF(this.centersTitleWithinRightImageInset)(() =>
                    bounds.rectangleWithInsets(caretSize + caretGap, caretSize + caretGap, 0, 0)
                ).ELSE_IF(titleIsRightAligned)(() =>
                    bounds.rectangleWithInsets(0, caretSize + caretGap, 0, 0)
                ).ELSE(() =>
                    bounds
                )
            }

            const desiredCaretX = IF(this.centersTitleWithinRightImageInset)(() =>
                bounds.x + bounds.width * 0.5 + titleWidth * 0.5 + caretGap
            ).ELSE_IF(titleIsRightAligned)(() =>
                bounds.max.x - buttonContentInset - caretSize
            ).ELSE(() =>
                bounds.x + buttonContentInset + titleWidth + caretGap
            )

            const caretX = [
                bounds.x,
                [desiredCaretX, bounds.max.x - caretSize].min()
            ].max()
            this._rightImageView.frame = bounds
                .rectangleWithX(caretX)
                .rectangleWithWidth(caretSize)
                .rectangleWithHeight(caretSize, 0.5)
            this._rightImageView.moveToTopOfSuperview()

        }

    }
    
    
    override intrinsicContentHeight(constrainingWidth = 0): number {
        
        const activeView: UIView | undefined = IF(this._variant == "label")(() =>
            this._titleLabel as UIView | undefined
        ).ELSE_IF(this._variant == "button")(() =>
            this._buttonView as UIView | undefined
        ).ELSE(() =>
            this._customView as UIView | undefined
        )
        
        const contentWidth = Math.max(0, constrainingWidth
            - this._contentInsets.left
            - this._contentInsets.right)
        const buttonTitleWidth = Math.max(
            0,
            contentWidth - (this._buttonView?.contentPadding ?? 0) * 2
        )
        const contentHeight = IF(this._variant == "button" && this._buttonView?.titleLabel)(() =>
            this._buttonView!.titleLabel.intrinsicContentHeight(buttonTitleWidth)
        ).ELSE(() =>
            activeView?.intrinsicContentHeight(contentWidth) ?? 0
        )
        
        return [
            contentHeight + this._contentInsets.top + this._contentInsets.bottom,
            this.minHeight
        ].max()
        
    }
    
    
}
